import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import { PageHeader } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { Tabs } from "antd";
import { Save } from "lucide-react";
import { useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import HRMSNavigationRoutes from "../../utils/navigationRoutes";

const PerformanceFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const schemaDetails = "Appraisal Template Hrms";
  const schemaKRA = "Hrms Kra";
  const schemaCriteria = "Criteria";
  const schemaAppraisal = "Hrms Appraisal";
  const schemaAppraisalCycle = "Hrms Appraisal Cycle";
  const schemaemployeeperformancefeedback =
    "Hrms Employee Performance Feedback";
  const schemaempromotion = "emp promotion";
  const schemahrmsappraisaloverview = "Hrms Appraisal Overview";

  // Create separate forms for each tab (mirrors Employee flow)
  const formDetails = useMemo(() => createForm({ validateFirst: true }), []);
  const formKRA = useMemo(() => createForm({ validateFirst: true }), []);
  const formCriteria = useMemo(() => createForm({ validateFirst: true }), []);
  const formAppraisal = useMemo(() => createForm({ validateFirst: true }), []);
  const formAppraisalCycle = useMemo(
    () => createForm({ validateFirst: true }),
    []
  );
  const formFeedback = useMemo(() => createForm({ validateFirst: true }), []);
  const formPromotion = useMemo(() => createForm({ validateFirst: true }), []);

  const formDetailsRef = useRef<{ handleSave: () => Promise<any> }>(null);
  const formKRARef = useRef<{ handleSave: () => Promise<any> }>(null);
  const formCriteriaRef = useRef<{ handleSave: () => Promise<any> }>(null);
  const formAppraisalRef = useRef<{ handleSave: () => Promise<any> }>(null);
  const formAppraisalCycleRef = useRef<{ handleSave: () => Promise<any> }>(
    null
  );
  const formFeedbackRef = useRef<{ handleSave: () => Promise<any> }>(null);
  const formPromotionRef = useRef<{ handleSave: () => Promise<any> }>(null);

  // Set initial values in edit mode so child components know the record id
  if (isEditMode) {
    formDetails.setInitialValues({ id });
    formKRA.setInitialValues({ id });
    formCriteria.setInitialValues({ id });
    formAppraisal.setInitialValues({ id });
    formAppraisalCycle.setInitialValues({ id });
    formFeedback.setInitialValues({ id });
    formPromotion.setInitialValues({ id });
  }

  const handleSaveAndRedirect = async (formRef: any) => {
    try {
      await formRef.current?.handleSave();
      notify.success("Performance record saved successfully");
      navigate("/hrms" + HRMSNavigationRoutes.PERFORMANCE_LIST);
    } catch (error) {
      notify.error("Failed to save performance record");
      console.error("Save error:", error);
    }
  };

  return (
    <>
      <PageHeader
        className="custom-page-header"
        title={isEditMode ? "Edit Performance" : "Create Performance"}
        onBack={() => navigate("/hrms" + HRMSNavigationRoutes.PERFORMANCE_LIST)}
        extra={[
          <CustomButton
            key="save"
            type="primary"
            icon={<Save size={16} />}
            onClick={() => handleSaveAndRedirect(formDetailsRef)}
          >
            {isEditMode ? "Update" : "Save"}
          </CustomButton>,
        ]}
      />

      <Tabs
        style={{ marginTop: 24 }}
        items={[
          {
            key: "1",
            label: "Appraisal Template",
            children: (
              <>
                <SchemaFormAutoSubmit
                  form={formDetails}
                  schemaName={schemaDetails}
                  recordId={id}
                  ref={formDetailsRef}
                />
                {/* per-tab save removed — use header Save */}
              </>
            ),
          },
          {
            key: "2",
            label: "KRA",
            children: (
              <>
                <SchemaFormAutoSubmit
                  form={formKRA}
                  schemaName={schemaKRA}
                  recordId={id}
                  ref={formKRARef}
                />
                {/* per-tab save removed — use header Save */}
              </>
            ),
          },
          {
            key: "3",
            label: "Feedback Criteria",
            children: (
              <>
                <SchemaFormAutoSubmit
                  form={formCriteria}
                  schemaName={schemaCriteria}
                  recordId={id}
                  ref={formCriteriaRef}
                />
                {/* per-tab save removed — use header Save */}
              </>
            ),
          },
          {
            key: "4",
            label: "Appraisal",
            children: (
              <>
                <SchemaFormAutoSubmit
                  form={formAppraisal}
                  schemaName={schemaAppraisal}
                  recordId={id}
                  ref={formAppraisalRef}
                />
                {/* per-tab save removed — use header Save */}
              </>
            ),
          },
          {
            key: "5",
            label: "Appraisal Cycle",
            children: (
              <>
                <SchemaFormAutoSubmit
                  form={formAppraisalCycle}
                  schemaName={schemaAppraisalCycle}
                  recordId={id}
                  ref={formAppraisalCycleRef}
                />
                {/* per-tab save removed — use header Save */}
              </>
            ),
          },
          {
            key: "6",
            label: "Performance Feedback",
            children: (
              <>
                <SchemaFormAutoSubmit
                  form={formFeedback}
                  schemaName={schemaemployeeperformancefeedback}
                  recordId={id}
                  ref={formFeedbackRef}
                />
                {/* per-tab save removed — use header Save */}
              </>
            ),
          },
          {
            key: "7",
            label: "Promotion",
            children: (
              <SchemaFormAutoSubmit
                form={formPromotion}
                schemaName={schemaempromotion}
                recordId={id}
                ref={formPromotionRef}
              />
            ),
          },
          {
            key: "8",
            label: "Appraisal Overview",
            children: (
              <SchemaFormAutoSubmit
                form={formPromotion}
                schemaName={schemahrmsappraisaloverview}
                recordId={id}
                ref={formPromotionRef}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default PerformanceFormPage;
